<template>
    <div class="mt-table" :data-loading="loading">
        <h3 class="section-title" v-if="!!title">{{ title }}</h3>

        <Paginator
            v-if="showPaginator"
            :currentPage.sync="computedCurrentPage"
            :page-size="pageSize"
            :item-count="totalCount"
        />

        <slot name="table-head" />

        <div :class="{ 'loading-container': loading }">
            <table v-if="hasRows">
                <thead :class="{ hidden: hideHeaders }">
                    <slot name="preheader" />
                    <tr>
                        <slot />
                    </tr>
                </thead>
                <TableRows
                    :rows="rows"
                    :link="link"
                    :rowClass="rowClass"
                    :highlightedItems="highlightedItems"
                    :keyFunc="keyFunc"
                    @click="rowClicked"
                >
                    <slot />
                </TableRows>
            </table>

            <slot v-else name="empty">{{ emptyMessage }}</slot>

            <div v-if="showPaginator" class="footer-paginator">
                <Paginator :currentPage.sync="computedCurrentPage" :page-size="pageSize" :item-count="totalCount" />
            </div>

            <loading :active="loading" :is-full-page="false" />
        </div>

        <slot name="table-footer" />
    </div>
</template>
<script>
import Paginator from '../paginator.vue'
import TableRows from './_rows'

export default {
    name: 'MtTable',
    components: {
        Paginator,
        TableRows,
    },
    provide() {
        return {
            onSortChange: this.onSortChange,
            currentSort: this.reactiveSort,
            table: this,
        }
    },
    props: {
        rows: {
            type: Array,
            default() {
                return []
            },
        },
        sort: {
            type: Object,
            default() {
                return {
                    field: null,
                    direction: null,
                }
            },
        },
        paginated: {
            type: Boolean,
            default: false,
        },
        pageSize: {
            type: Number,
            default: 20,
        },
        page: {
            type: Number,
            default: 1,
        },
        totalCount: {
            type: Number,
            default: null,
        },
        emptyMessage: {
            type: String,
            default: '',
        },
        link: {
            type: Function,
            default: null,
        },
        hideHeaders: {
            type: Boolean,
            default: false,
        },
        rowClass: {
            type: Function,
            default: null,
        },
        title: {
            type: String,
            default: null,
        },
        loading: {
            type: Boolean,
            required: false,
            default: false,
        },
        highlightedItems: {
            type: Array,
            default: () => [],
        },
        keyFunc: {
            type: Function,
        },
    },
    data() {
        return {
            reactiveSort: {
                field: this.sort.field,
                direction: this.sort.direction,
            },
            columns: [],
        }
    },
    watch: {
        link() {
            this.$forceUpdate()
        },
        sort() {
            Object.assign(this.reactiveSort, this.sort)
        },
    },
    methods: {
        rowClicked(row) {
            this.$emit('rowClicked', row)
        },
        onSortChange(value) {
            this.$emit('update:sort', value)
        },
        updateColumns() {
            // the columns tell us when they are mounted/destroyed
            // by calling this method; update our collection in
            // response
            const previous = this.columns
            const current =
                this.$slots.default?.filter(vnode => vnode.componentInstance)?.map(vnode => vnode.componentInstance) ??
                []

            if (
                previous.length != current.length ||
                previous.find((component, index) => current[index] !== component)
            ) {
                this.columns = current

                this.$emit('update:columns', this.columns)
            }
        },
    },
    computed: {
        hasRows() {
            return this.rows.length > 0
        },
        computedCurrentPage: {
            get() {
                return this.page
            },
            set(value) {
                this.$emit('update:page', value)
            },
        },
        showPaginator() {
            return this.paginated && this.rows && this.rows.length > 0
        },
    },
}
</script>
<style>
.mt-table {
    position: relative;

    &[data-loading='true'] .loading-container {
        min-height: 100px;
    }

    table {
        page-break-inside: avoid;
        width: 100%;
    }

    thead.hidden {
        display: none;
    }

    td {
        padding: 8px 16px;
    }

    tr {
        height: 40px;
    }

    th {
        padding: 8px 8px 8px 16px;
    }

    th.detail + th.checkbox,
    td.detail + td.checkbox {
        padding-left: 8px;
    }

    td {
        border-bottom: 1px solid var(--color-primary-200);
        font-size: 14px;
    }

    th {
        text-align: left;
        color: rgba(51, 51, 51, 0.64);
        font-size: 12px;
        font-weight: var(--weight-bold);
        text-transform: uppercase;
        background-color: var(--color-primary-100);
        border-right: 1px solid var(--color-light);
        line-height: 24px;

        &:last-child {
            border-right: none;
        }

        & > div.column-label {
            display: flex;
            justify-content: space-between;
            overflow: hidden;
            text-overflow: ellipsis;
            width: 100%;

            span {
                flex-grow: 1;
                text-overflow: ellipsis;
                white-space: nowrap;
            }

            .arrow {
                flex-shrink: 0;
            }
        }
    }

    .empty-message {
        text-align: center;
    }

    &.light-table {
        thead {
            th {
                background-color: var(--color-light);
                border-right: none;
                border-bottom: 1px solid var(--color-alpha-1);
                font-weight: var(--weight-black);
                line-height: 16px;
            }
        }

        tbody {
            a {
                font-weight: var(--weight-bold);
                color: var(--color-secondary-700);
            }
        }
    }

    & > .loading-container {
        clear: both;
    }

    .highlighted {
        background-color: var(--color-secondary-000);

        &:hover {
            background-color: var(--color-secondary-025);
        }
    }
}

.mt-table td.detail a[data-expanded='true'] {
    transform: rotate(90deg);
}
</style>
